import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL;

// Formats FastAPI error details (422s arrive as arrays, not strings).
function formatApiErrorDetail(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail
      .map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e)))
      .filter(Boolean)
      .join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

export default function LoginPage() {
  const navigate = useNavigate();
  const [mode, setMode] = useState("signin"); // signin | forgot | otp | reset-done
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [otp, setOtp] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [busy, setBusy] = useState(false);

  const resetForm = () => {
    setError("");
    setNotice("");
    setBusy(false);
  };

  const submitSignin = async (e) => {
    e.preventDefault();
    if (busy) return;
    setBusy(true);
    setError("");
    try {
      const { data } = await axios.post(`${API}/api/auth/login`, {
        email: email.trim(),
        password,
      });
      if (!data?.ok) throw new Error("login failed");
      // Same session as the magic-link flow — the dashboard picks it up identically.
      localStorage.setItem("grapho_customer_email", data.customer.email);
      navigate("/dashboard");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || "Invalid email or password.");
      setBusy(false);
    }
  };

  const submitForgot = async (e) => {
    e.preventDefault();
    if (busy) return;
    setBusy(true);
    setError("");
    try {
      const { data } = await axios.post(`${API}/api/auth/forgot-password`, {
        email: email.trim(),
      });
      if (!data?.ok) throw new Error("request failed");
      setNotice("If that email has an account, a 6-digit code is on its way. It expires in 10 minutes.");
      setMode("otp");
      setBusy(false);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail));
      setBusy(false);
    }
  };

  const submitOtp = async (e) => {
    e.preventDefault();
    if (busy) return;
    setBusy(true);
    setError("");
    try {
      const { data } = await axios.post(`${API}/api/auth/reset-password-otp`, {
        email: email.trim(),
        otp: otp.trim(),
        new_password: newPassword,
      });
      if (!data?.ok) throw new Error("reset failed");
      setMode("reset-done");
      setPassword("");
      setOtp("");
      setNewPassword("");
      setBusy(false);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail));
      setBusy(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-[#070B14] px-6">
      <form
        onSubmit={mode === "signin" ? submitSignin : mode === "forgot" ? submitForgot : submitOtp}
        data-testid="login-form"
        className="glass w-full max-w-md rounded-3xl p-8"
      >
        <p className="font-mono text-[11px] font-semibold uppercase tracking-[0.25em] text-amber-400/90">
          Grapho Studio
        </p>
        <p className="mt-2 text-sm text-slate-400">
          Your overnight design studio. Brief it tonight. Download it tomorrow.
        </p>

        {mode === "signin" && (
          <>
            <h1 className="mt-5 text-2xl font-bold tracking-tight text-white">Sign in</h1>
            <p className="mt-2 text-sm text-slate-400">Sign in with your email and password.</p>

            <label className="mt-6 block text-xs font-medium uppercase tracking-wider text-slate-400">
              Email
              <input
                data-testid="login-email-input"
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@yourshop.com"
                className="mt-2 w-full rounded-xl border border-white/10 bg-white/5 px-4 py-3 text-sm text-white placeholder:text-slate-600 focus:border-amber-400/60 focus:outline-none"
              />
            </label>

            <label className="mt-4 block text-xs font-medium uppercase tracking-wider text-slate-400">
              Password
              <input
                data-testid="login-password-input"
                type="password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                className="mt-2 w-full rounded-xl border border-white/10 bg-white/5 px-4 py-3 text-sm text-white placeholder:text-slate-600 focus:border-amber-400/60 focus:outline-none"
              />
            </label>

            {error && (
              <p data-testid="login-error" className="mt-4 text-sm text-rose-400">
                {error}
              </p>
            )}

            <button
              data-testid="login-submit-button"
              type="submit"
              disabled={busy}
              className="mt-6 w-full rounded-xl bg-amber-400 px-5 py-3 text-sm font-bold text-[#070B14] transition hover:bg-amber-300 disabled:opacity-60"
            >
              {busy ? "Signing in..." : "Sign in to your studio"}
            </button>

            <div className="mt-5 flex items-center justify-between text-sm">
              <button
                type="button"
                data-testid="login-forgot-link"
                onClick={() => {
                  setMode("forgot");
                  resetForm();
                }}
                className="text-slate-400 transition-colors hover:text-amber-300"
              >
                Forgot password?
              </button>
              <Link
                to="/signup"
                data-testid="login-signup-link"
                className="text-amber-300 transition-colors hover:text-amber-200"
              >
                Don&apos;t have an account? Create one
              </Link>
            </div>
          </>
        )}

        {mode === "forgot" && (
          <>
            <h1 className="mt-5 text-2xl font-bold tracking-tight text-white">Reset your password</h1>
            <p className="mt-2 text-sm text-slate-400">
              Enter your account email and we&apos;ll send you a 6-digit code.
            </p>

            <label className="mt-6 block text-xs font-medium uppercase tracking-wider text-slate-400">
              Email
              <input
                data-testid="forgot-email-input"
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@yourshop.com"
                className="mt-2 w-full rounded-xl border border-white/10 bg-white/5 px-4 py-3 text-sm text-white placeholder:text-slate-600 focus:border-amber-400/60 focus:outline-none"
              />
            </label>

            {notice && (
              <p data-testid="forgot-notice" className="mt-4 text-sm text-emerald-400">
                {notice}
              </p>
            )}
            {error && (
              <p data-testid="login-error" className="mt-4 text-sm text-rose-400">
                {error}
              </p>
            )}

            <button
              data-testid="forgot-submit-button"
              type="submit"
              disabled={busy}
              className="mt-6 w-full rounded-xl bg-amber-400 px-5 py-3 text-sm font-bold text-[#070B14] transition hover:bg-amber-300 disabled:opacity-60"
            >
              {busy ? "Sending code..." : "Send reset code"}
            </button>

            <div className="mt-5 text-sm">
              <button
                type="button"
                data-testid="forgot-back-link"
                onClick={() => {
                  setMode("signin");
                  resetForm();
                }}
                className="text-slate-400 transition-colors hover:text-amber-300"
              >
                Back to sign in
              </button>
            </div>
          </>
        )}

        {mode === "otp" && (
          <>
            <h1 className="mt-5 text-2xl font-bold tracking-tight text-white">Enter your code</h1>
            <p className="mt-2 text-sm text-slate-400">
              Check <span className="text-slate-200">{email}</span> for the 6-digit code.
            </p>

            <label className="mt-6 block text-xs font-medium uppercase tracking-wider text-slate-400">
              6-digit code
              <input
                data-testid="otp-input"
                type="text"
                inputMode="numeric"
                pattern="[0-9]*"
                maxLength={6}
                required
                value={otp}
                onChange={(e) => setOtp(e.target.value.replace(/\D/g, ""))}
                placeholder="000000"
                className="mt-2 w-full rounded-xl border border-white/10 bg-white/5 px-4 py-3 text-center text-lg font-bold tracking-[0.4em] text-white placeholder:text-slate-600 focus:border-amber-400/60 focus:outline-none"
              />
            </label>

            <label className="mt-4 block text-xs font-medium uppercase tracking-wider text-slate-400">
              New password
              <input
                data-testid="otp-new-password-input"
                type="password"
                required
                minLength={8}
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                placeholder="At least 8 characters"
                className="mt-2 w-full rounded-xl border border-white/10 bg-white/5 px-4 py-3 text-sm text-white placeholder:text-slate-600 focus:border-amber-400/60 focus:outline-none"
              />
            </label>

            {error && (
              <p data-testid="login-error" className="mt-4 text-sm text-rose-400">
                {error}
              </p>
            )}

            <button
              data-testid="otp-submit-button"
              type="submit"
              disabled={busy}
              className="mt-6 w-full rounded-xl bg-amber-400 px-5 py-3 text-sm font-bold text-[#070B14] transition hover:bg-amber-300 disabled:opacity-60"
            >
              {busy ? "Saving..." : "Set new password"}
            </button>

            <div className="mt-5 text-sm">
              <button
                type="button"
                data-testid="otp-back-link"
                onClick={() => {
                  setMode("signin");
                  resetForm();
                }}
                className="text-slate-400 transition-colors hover:text-amber-300"
              >
                Back to sign in
              </button>
            </div>
          </>
        )}

        {mode === "reset-done" && (
          <>
            <h1 className="mt-5 text-2xl font-bold tracking-tight text-white">Password updated</h1>
            <p data-testid="reset-success" className="mt-3 text-sm text-emerald-400">
              Your new password is saved. Sign in with it below.
            </p>
            <button
              data-testid="reset-done-signin-button"
              type="button"
              onClick={() => {
                setMode("signin");
                resetForm();
              }}
              className="mt-6 w-full rounded-xl bg-amber-400 px-5 py-3 text-sm font-bold text-[#070B14] transition hover:bg-amber-300"
            >
              Back to sign in
            </button>
          </>
        )}
      </form>
    </div>
  );
}
